import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, BULAN, uploadFile, fileUrl, formatRupiah , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Upload, X, Download } from "lucide-react";
import { API_URL } from "@/lib/api";

const empty = { guru_id: "", bulan: "01", tahun: new Date().getFullYear(), jumlah: "", tanggal_bayar: new Date().toISOString().slice(0,10), slip_file_id: null, slip_url: "" };

function GajiExcelControls({ onReload }) {
  const [busy, setBusy] = React.useState(false);
  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/excel/gaji/template`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/gaji/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted}`);
      if (data.errors?.length) toast.error(`Error: ${data.errors.join(" | ")}`);
      onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="dl-template-gaji">
        <Download className="w-4 h-4" /> Download Template Gaji
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="upload-gaji-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Massal Gaji (Excel)"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="upload-gaji-input" />
      </label>
    </div>
  );
}

export default function AdminGaji() {
  const [items, setItems] = useState([]);
  const [guru, setGuru] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [fGuru, setFGuru] = useState(""); const [fBulan, setFBulan] = useState(""); const [fTahun, setFTahun] = useState("");
  const [applied, setApplied] = useState({ guru: "", bulan: "", tahun: "" });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (applied.guru) params.guru_id = applied.guru;
    if (applied.bulan) params.bulan = applied.bulan;
    if (applied.tahun) params.tahun = applied.tahun;
    const { data } = await api.get("/gaji", { params });
    setItems(data);
    const { data: g } = await api.get("/guru", { params: { status: "aktif" } });
    setGuru(g);
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied({ guru: fGuru, bulan: fBulan, tahun: fTahun });
  const resetFilter = () => { setFGuru(""); setFBulan(""); setFTahun(""); setApplied({ guru: "", bulan: "", tahun: "" }); };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const res = await uploadFile(f); setForm({ ...form, slip_file_id: res.id }); toast.success("Slip diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const slipUrl = (form.slip_url || "").trim();
      if (slipUrl) {
        const lower = slipUrl.toLowerCase();
        const ok = (lower.startsWith("http://") || lower.startsWith("https://")) && (lower.includes("drive.google.com") || lower.includes("docs.google.com"));
        if (!ok) return toast.error("Link Slip harus URL Google Drive/Docs");
      }
      const payload = { ...form, slip_url: slipUrl || null, tahun: parseInt(form.tahun), jumlah: parseFloat(form.jumlah) };
      if (editId) await api.put(`/gaji/${editId}`, payload); else await api.post("/gaji", payload);
      toast.success("Tersimpan");
      setShowForm(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus data gaji?")) return; await api.delete(`/gaji/${id}`); toast.success("Dihapus"); load(); };
  const startEdit = (it) => { setForm({ ...empty, ...it, slip_url: it.slip_url || "" }); setEditId(it.id); setShowForm(true); };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Kelola Data Gaji" action={
        <div className="flex gap-2">
          <a href={`${API_URL}/export/gaji${applied.bulan || applied.tahun ? `?${[applied.bulan && `bulan=${applied.bulan}`, applied.tahun && `tahun=${applied.tahun}`].filter(Boolean).join("&")}` : ""}`} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="export-gaji">
            <Download className="w-4 h-4" /> Export PDF
          </a>
          <button onClick={() => { setShowForm(true); setForm(empty); setEditId(null); }} className="pp-btn-primary" data-testid="open-gaji-form">
            <Plus className="w-4 h-4" /> Input Gaji Guru
          </button>
        </div>
      } />

      <GajiExcelControls onReload={load} />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3">
          <select className="pp-input" value={fGuru} onChange={(e) => setFGuru(e.target.value)}>
            <option value="">Semua guru</option>{guru.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
          <select className="pp-input" value={fBulan} onChange={(e) => setFBulan(e.target.value)}>
            <option value="">Semua bulan</option>{BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
          </select>
          <input className="pp-input" type="number" placeholder="Tahun" value={fTahun} onChange={(e) => setFTahun(e.target.value)} />
          <div className="flex gap-2"><button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="gaji-filter-apply">Terapkan</button><button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="gaji-filter-reset">Reset</button></div>
        </div>
      </section>

      {showForm && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold">{editId ? "Edit Gaji" : "Input Gaji Guru"}</h3>
            <button onClick={() => { setShowForm(false); setForm(empty); setEditId(null); }}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className="pp-label">Guru *</label>
              <select required className="pp-input" value={form.guru_id} onChange={(e) => setForm({...form, guru_id: e.target.value})} data-testid="gaji-guru">
                <option value="">Pilih guru</option>
                {guru.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select></div>
            <div><label className="pp-label">Bulan *</label>
              <select className="pp-input" value={form.bulan} onChange={(e) => setForm({...form, bulan: e.target.value})}>
                {BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
              </select></div>
            <div><label className="pp-label">Tahun *</label><input required type="number" className="pp-input" value={form.tahun} onChange={(e) => setForm({...form, tahun: e.target.value})} /></div>
            <div><label className="pp-label">Jumlah Diterima (Rp) *</label><input required type="number" className="pp-input" value={form.jumlah} onChange={(e) => setForm({...form, jumlah: e.target.value})} data-testid="gaji-jumlah" /></div>
            <div><label className="pp-label">Tanggal Bayar *</label><input required type="date" className="pp-input" value={form.tanggal_bayar} onChange={(e) => setForm({...form, tanggal_bayar: e.target.value})} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Link Slip Gaji (Google Drive)</label>
              <input
                type="url"
                className="pp-input"
                placeholder="https://drive.google.com/file/d/..."
                value={form.slip_url || ""}
                onChange={(e) => setForm({ ...form, slip_url: e.target.value })}
                data-testid="gaji-slip-url"
              />
              <p className="text-xs text-slate-500 mt-1">Tempel link Google Drive/Docs slip gaji (opsional). Kolom ini menggantikan upload file untuk menghemat storage.</p>
            </div>
            <div className="sm:col-span-2"><label className="pp-label">Slip Gaji (PDF) <span className="text-slate-400 font-normal">(opsional, jika tidak pakai link Drive)</span></label>
              <label className="pp-btn-secondary cursor-pointer"><Upload className="w-4 h-4" /> {uploading ? "Mengunggah…" : (form.slip_file_id ? "Ganti Slip" : "Pilih File")}
                <input type="file" accept=".pdf,image/*" onChange={handleUpload} className="hidden" />
              </label>
              {form.slip_file_id && <span className="ml-3 text-sm text-emerald-600">✓ Slip siap</span>}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2 pt-4 border-t border-slate-200">
              <button type="button" onClick={() => { setShowForm(false); setForm(empty); setEditId(null); }} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-gaji">Simpan Data Gaji</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gaji-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Nama Guru</th>
              <th className="text-left px-5 py-3">Bulan/Tahun</th>
              <th className="text-right px-5 py-3">Jumlah</th>
              <th className="text-left px-5 py-3">Tgl Bayar</th>
              <th className="text-left px-5 py-3">Slip</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="pp-row">
                  <td className="px-5 py-3 font-medium">{it.nama_guru}</td>
                  <td className="px-5 py-3">{BULAN.find(b => b.v === it.bulan)?.label} {it.tahun}</td>
                  <td className="px-5 py-3 text-right font-mono">{formatRupiah(it.jumlah)}</td>
                  <td className="px-5 py-3 text-slate-600">{formatTanggal(it.tanggal_bayar)}</td>
                  <td className="px-5 py-3">{
                    it.slip_url
                      ? <a href={it.slip_url} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-xs" data-testid={`gaji-slip-url-${it.id}`}><FileText className="w-3.5 h-3.5" />Drive</a>
                      : it.slip_file_id
                        ? <a href={fileUrl(it.slip_file_id)} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-xs"><FileText className="w-3.5 h-3.5" />Lihat</a>
                        : <span className="text-slate-400 italic text-xs">—</span>
                  }</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => startEdit(it)} className="pp-btn-ghost text-xs"><Pencil className="w-3.5 h-3.5" />Edit</button>
                      <button onClick={() => del(it.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
